import { useState } from 'react';
import { cn } from '@pixlide/ui';
import { useTemplatePreview } from '@/api/queries/templates';
import { errorMessage, formatAspectClasses } from '@/features/templates/template-ui';

/**
 * Positions proposées dans le déroulé. Ni 0 ni 1 exactement : l'ouverture d'une animation
 * est souvent un fondu depuis le noir, et sa dernière frame une sortie d'écran.
 */
const POSITIONS = [
  { label: 'Début', progress: 0.05 },
  { label: '25 %', progress: 0.25 },
  { label: 'Milieu', progress: 0.5 },
  { label: '75 %', progress: 0.75 },
  { label: 'Fin', progress: 0.95 },
] as const;

/**
 * Aperçu du template rendu par le moteur de PRODUCTION (Skottie côté serveur), et non par
 * un lecteur Lottie dans le navigateur : c'est précisément l'écart entre les deux (police
 * absente du serveur, effet non pris en charge) que l'admin doit voir avant de publier.
 * Une seule position rendue à la fois, à la demande, chacune mise en cache.
 */
export function TemplatePreviewPanel({ templateId, format }: { templateId: string; format: 0 | 1 | 2 | 3 }) {
  const [progress, setProgress] = useState<number>(0.5);
  const preview = useTemplatePreview(templateId, progress, true);

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-ink-50 p-3">
      <div role="group" aria-label="Position dans l'animation" className="flex flex-wrap gap-1">
        {POSITIONS.map((position) => (
          <button
            key={position.label}
            type="button"
            aria-pressed={progress === position.progress}
            onClick={() => setProgress(position.progress)}
            className={cn(
              'rounded-md px-2 py-1 text-xs',
              progress === position.progress ? 'bg-ink-900 text-white' : 'bg-white text-ink-500 ring-1 ring-ink-100',
            )}
          >
            {position.label}
          </button>
        ))}
      </div>

      <div className={cn('mx-auto w-full max-w-xs overflow-hidden rounded-md bg-black', formatAspectClasses[format])}>
        {preview.data ? (
          <img
            src={preview.data.imageDataUri}
            alt={`Aperçu du template à ${Math.round(progress * 100)} % de l'animation`}
            className="size-full object-contain"
          />
        ) : null}
      </div>

      {preview.isFetching ? <p className="text-xs text-ink-500">Rendu de l’aperçu…</p> : null}
      {preview.isError ? (
        <p className="text-xs text-danger-500">{errorMessage(preview.error, 'Impossible de rendre l’aperçu.')}</p>
      ) : null}
    </div>
  );
}
